import pushToDataLayer, { AdditionalData, PushEventType, TrackingEvent } from './pushToDataLayer';
import { PasswordResetMode } from '@spa/components/login-register/send-password-reset/PasswordResetMode';

export enum CustomerActionsType {
  REGISTRATION = 'registration',
  CHECKOUT_SUCCESSFUL = 'checkout_successful',
  BREADCRUMBS = 'breadcrumbs',
  LOGIN = 'login',
  ERROR = 'website_error',
  NEWSLETTER_REGISTRATION = 'newsletter_registration',
  NEWSLETTER_DOI = 'newsletter_doi',
  PASSWORD_RESET_REQUEST = 'password_reset_request'
}

export enum CustomerActionsErrorType {
  LOGIN = 'login_error',
  REGISTRATION = 'registration_error'
}

export enum CustomerAuthenticationMethod {
  EMAIL = 'email'
}

interface RegistrationEvent {
  readonly type: CustomerActionsType.REGISTRATION;
}

interface BreadcrumbsEvent {
  readonly type: CustomerActionsType.BREADCRUMBS;
  readonly category?: string;
  readonly subCategory?: string;
  readonly breadcrumb?: string;
}

interface LoginEvent {
  readonly type: CustomerActionsType.LOGIN;
}

interface CheckoutEvent {
  readonly type: CustomerActionsType.CHECKOUT_SUCCESSFUL;
  readonly country?: string;
  readonly city?: string;
  readonly zip?: string;
}

interface ErrorEvent {
  readonly type: CustomerActionsType.ERROR;
  readonly errorType: CustomerActionsErrorType;
  readonly errorMessage: string;
}

interface NewsletterRegistrationEvent {
  readonly type: CustomerActionsType.NEWSLETTER_REGISTRATION;
}

interface NewsletterDoiEvent {
  readonly type: CustomerActionsType.NEWSLETTER_DOI;
}

interface PasswordResetRequestEvent {
  readonly type: CustomerActionsType.PASSWORD_RESET_REQUEST;
  readonly resetMode: PasswordResetMode;
}

export type TrackCustomerActionsType =
  | RegistrationEvent
  | CheckoutEvent
  | LoginEvent
  | ErrorEvent
  | BreadcrumbsEvent
  | NewsletterRegistrationEvent
  | NewsletterDoiEvent
  | PasswordResetRequestEvent;

export const trackCustomerActions = (
  eventType: TrackCustomerActionsType,
  anonymousId: string | null,
  authenticatedId: string | null
): void => {
  const userId: string | null = authenticatedId ?? anonymousId;
  let additionalData: AdditionalData;

  switch (eventType.type) {
    case CustomerActionsType.REGISTRATION:
      additionalData = {
        event: eventType.type,
        authentication_method: CustomerAuthenticationMethod.EMAIL,
        user_id: userId,
        ecVisitorRegister: 0,
        ecVisitorId: userId
      };
      break;

    case CustomerActionsType.LOGIN:
      additionalData = {
        event: eventType.type,
        authentication_method: CustomerAuthenticationMethod.EMAIL,
        user_id: userId,
        ecVisitorLogin: 0,
        ecVisitorId: userId
      };
      break;

    case CustomerActionsType.CHECKOUT_SUCCESSFUL:
      additionalData = {
        event: eventType.type,
        user_id: userId,
        ecCustomerId: userId,
        ecCustomerLocation: `${eventType.country}/${eventType.city}/${eventType.zip}`
      };
      break;

    case CustomerActionsType.BREADCRUMBS:
      additionalData = {
        emosUrl: `${window.location.origin}/assets/js/emos3.js`,
        apnxUrl: `${window.location.origin}/assets/js/ec_apnx.js`,
        ecCategory: eventType.category,
        ecSubCategory: eventType.subCategory,
        breadcrumb: eventType.breadcrumb
      };
      break;

    case CustomerActionsType.ERROR:
      additionalData = {
        event: eventType.type,
        error_type: eventType.errorType,
        error_message: eventType.errorMessage,
        ...(eventType.errorType == CustomerActionsErrorType.LOGIN
          ? { ecVisitorLogin: 1 }
          : { ecVisitorRegister: 1 })
      };
      break;

    case CustomerActionsType.NEWSLETTER_REGISTRATION:
      additionalData = {
        event: eventType.type,
        authentication_method: 'email',
        user_id: userId
      };
      break;

    case CustomerActionsType.NEWSLETTER_DOI:
      additionalData = {
        event: eventType.type,
        authentication_method: 'email',
        user_id: userId
      };
      break;

    case CustomerActionsType.PASSWORD_RESET_REQUEST:
      additionalData = {
        event: eventType.type,
        reset_mode: eventType.resetMode,
        user_id: userId
      };
      break;
  }

  const customerEvent: PushEventType = {
    type: TrackingEvent.CUSTOMER,
    ...(anonymousId !== null && { anonymousId }),
    ...(authenticatedId !== null && { authenticatedId }),
    event: eventType.type,
    ...additionalData
  };

  pushToDataLayer(customerEvent);
};

export default trackCustomerActions;
